// Custom hook for managing liveness session data and API calls

import { useState, useEffect, useCallback } from 'react';
import apiClient, { API_ENDPOINTS } from '../services/api';
import { SessionDetails } from '../types/livenessTypes';

/**
 * Custom hook to fetch and manage liveness session data
 * @param sessionId The ID of the liveness session
 * @param livenessSessionToken Session token used to authorize status requests
 * @returns Session state and control functions
 */
export const useLivenessSession = (
  sessionId: string | undefined,
  livenessSessionToken?: string | null
) => {
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [resultsLoading, setResultsLoading] = useState<boolean>(false);

  /**
   * Set an error message
   */
  const setErrorMessage = useCallback((message: string) => {
    console.error(message);
    setError(message);
  }, []);

  /**
   * Fetch session details from the API
   */
  const fetchSessionDetails = useCallback(async () => {
    if (!sessionId) {
      setErrorMessage('Session ID is missing');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get(
        API_ENDPOINTS.LIVENESS.SESSION_BY_ID(sessionId, livenessSessionToken ?? undefined)
      );
      setSessionDetails(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching session details:', err);
      setErrorMessage('Failed to fetch session details. The session may have expired.');
    } finally {
      setLoading(false);
    }
  }, [sessionId, livenessSessionToken, setErrorMessage]);

  /**
   * Fetch session results after liveness check is complete
   */
  const fetchSessionResults = useCallback(async () => {
    if (!sessionId) {
      setErrorMessage('Session ID is missing');
      return;
    }

    try {
      setResultsLoading(true);
      const response = await apiClient.get(
        API_ENDPOINTS.LIVENESS.SESSION_BY_ID(sessionId, livenessSessionToken ?? undefined)
      );
      setSessionDetails(response.data);
      setError(null);
      console.log('Liveness check results:', response.data);
    } catch (err) {
      console.error('Error fetching liveness check results:', err);
      setErrorMessage('Failed to fetch liveness check results. Please try again.');
    } finally {
      setResultsLoading(false);
    }
  }, [sessionId, livenessSessionToken, setErrorMessage]);

  // Initial fetch of session details
  useEffect(() => {
    fetchSessionDetails();
  }, [fetchSessionDetails]);

  return {
    sessionDetails,
    loading,
    error,
    resultsLoading,
    fetchSessionDetails,
    fetchSessionResults,
    setErrorMessage
  };
};
